import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Briefcase, MapPin, TrendingUp, Tag, Building2 } from 'lucide-react';

const DIMENSION_ICONS = {
  'Title Match': Briefcase,
  'Seniority': TrendingUp,
  'Location': MapPin,
  'Skills/Keywords': Tag,
  'Industry Relevance': Building2,
};

const CATEGORY_COLORS = {
  exact: { bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', label: 'Exact' },
  near: { bar: 'bg-emerald-400', badge: 'bg-emerald-50 text-emerald-600', label: 'Near' },
  close: { bar: 'bg-emerald-400', badge: 'bg-emerald-50 text-emerald-600', label: 'Close' },
  partial: { bar: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700', label: 'Partial' },
  related: { bar: 'bg-amber-300', badge: 'bg-amber-50 text-amber-600', label: 'Related' },
  strong: { bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', label: 'Strong' },
  weak: { bar: 'bg-orange-300', badge: 'bg-orange-50 text-orange-600', label: 'Weak' },
  match: { bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', label: 'Match' },
  nearby: { bar: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700', label: 'Nearby' },
  remote: { bar: 'bg-blue-400', badge: 'bg-blue-100 text-blue-700', label: 'Remote' },
  mismatch: { bar: 'bg-red-400', badge: 'bg-red-100 text-red-700', label: 'Mismatch' },
  none: { bar: 'bg-gray-300', badge: 'bg-gray-100 text-gray-500', label: 'None' },
  unknown: { bar: 'bg-gray-300', badge: 'bg-gray-100 text-gray-500', label: 'Unknown' },
  'n/a': { bar: 'bg-gray-200', badge: 'bg-gray-50 text-gray-400', label: 'N/A' },
};

export const ScoreBreakdown = ({ breakdown, totalScore, compact = false }) => {
  const [expanded, setExpanded] = useState(false);

  if (!breakdown || breakdown.length === 0) return null;

  const totalMax = breakdown.reduce((sum, d) => sum + Math.max(d.max_points, 0), 0);

  if (compact) {
    return (
      <div data-testid="score-breakdown-compact">
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="flex items-center gap-1 text-[9px] text-[#0A1F44]/60 hover:text-[#0A1F44] transition-colors"
          data-testid="score-breakdown-toggle"
        >
          {expanded ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
          <span className="underline decoration-dotted">Why this score?</span>
        </button>
        {expanded && (
          <div className="mt-1.5 p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] font-bold text-[#0A1F44] uppercase tracking-wider">Score Breakdown</span>
              <span className="text-[9px] font-mono text-gray-500">{totalScore}/{totalMax} pts</span>
            </div>
            {breakdown.map((dim, i) => {
              const cat = CATEGORY_COLORS[dim.category] || CATEGORY_COLORS['n/a'];
              const Icon = DIMENSION_ICONS[dim.dimension] || Tag;
              const pct = dim.max_points > 0 ? Math.max(0, (dim.points / dim.max_points) * 100) : 0;
              return (
                <div key={i} className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Icon className="h-2.5 w-2.5 text-gray-400" />
                      <span className="text-[9px] font-medium text-[#0A1F44]">{dim.dimension}</span>
                      <span className={`px-1 py-0 text-[7px] font-bold rounded ${cat.badge}`}>{cat.label}</span>
                    </div>
                    <span className={`text-[9px] font-mono font-bold ${dim.points > 0 ? 'text-emerald-600' : dim.points < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {dim.points > 0 ? '+' : ''}{dim.points}/{dim.max_points}
                    </span>
                  </div>
                  <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${cat.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[8px] text-gray-400 leading-tight pl-3.5">{dim.detail}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="score-breakdown-full">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-[#0A1F44]">Match Breakdown</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-bold ${totalScore >= 50 ? 'text-emerald-600' : totalScore >= 30 ? 'text-amber-600' : 'text-gray-500'}`}>{totalScore}</span>
          <span className="text-[9px] text-gray-400">/ {totalMax} pts</span>
        </div>
      </div>
      {breakdown.map((dim, i) => {
        const cat = CATEGORY_COLORS[dim.category] || CATEGORY_COLORS['n/a'];
        const Icon = DIMENSION_ICONS[dim.dimension] || Tag;
        const pct = dim.max_points > 0 ? Math.max(0, (dim.points / dim.max_points) * 100) : 0;
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Icon className="h-3 w-3 text-[#0A1F44]/50" />
                <span className="text-[10px] font-semibold text-[#0A1F44]">{dim.dimension}</span>
                <span className={`px-1.5 py-0.5 text-[7px] font-bold rounded ${cat.badge}`}>{cat.label}</span>
              </div>
              <span className={`text-[10px] font-mono font-bold ${dim.points > 0 ? 'text-emerald-600' : dim.points < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {dim.points > 0 ? '+' : ''}{dim.points}/{dim.max_points}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-300 ${cat.bar}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between pl-4">
              <p className="text-[8px] text-gray-500 leading-tight">{dim.detail}</p>
              {dim.comparison && (
                <p className="text-[8px] text-gray-400 italic">
                  {dim.comparison.candidate !== 'unknown' && dim.comparison.candidate !== 'unknown dept' ? dim.comparison.candidate : '?'}
                  {' vs '}
                  {dim.comparison.required !== 'any' ? dim.comparison.required : 'any'}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
